// Storefront — real product catalog + cart + checkout
import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

const products = () => fetch("/api/v1/product.template",{headers:{"X-Dev-Auth":"dev","X-Tenant-ID":"1"}}).then(r=>r.json()).then((r:any)=>r.ok?r.data:[]);
const partners = () => fetch("/api/v1/res.partner",{headers:{"X-Dev-Auth":"dev","X-Tenant-ID":"1"}}).then(r=>r.json()).then((r:any)=>r.ok?r.data:[]);
const createOrder = (d:any) => fetch("/api/v1/sale.order",{method:"POST",headers:{"X-Dev-Auth":"dev","X-Tenant-ID":"1","Content-Type":"application/json"},body:JSON.stringify(d)}).then(r=>r.json()).then((r:any)=>r.ok?r.data:null);
const createOrderLine = (d:any) => fetch("/api/v1/sale.order.line",{method:"POST",headers:{"X-Dev-Auth":"dev","X-Tenant-ID":"1","Content-Type":"application/json"},body:JSON.stringify(d)}).then(r=>r.json());

export default function Storefront() {
  const { data: allProducts } = useQuery({ queryKey: ["store-products"], queryFn: products });
  const { data: allPartners } = useQuery({ queryKey: ["store-partners"], queryFn: partners });
  const [cart, setCart] = useState<{productId:number;name:string;price:number;qty:number}[]>([]);
  const [search, setSearch] = useState("");
  const [partnerId, setPartnerId] = useState("");
  const [orderPlaced, setOrderPlaced] = useState<number|null>(null);
  const navigate = useNavigate();

  const filtered = allProducts?.filter((p:any)=>!search||p.name?.toLowerCase().includes(search.toLowerCase()))??[];
  const total = cart.reduce((s,i)=>s+i.price*i.qty,0);
  const cartCount = cart.reduce((s,i)=>s+i.qty,0);

  const addToCart = (p:any) => {
    const ex = cart.find(c=>c.productId===p.id);
    if(ex) setCart(cart.map(c=>c.productId===p.id?{...c,qty:c.qty+1}:c));
    else setCart([...cart,{productId:p.id,name:p.name,price:p.list_price??0,qty:1}]);
  };

  const checkout = async () => {
    if(!partnerId||cart.length===0)return;
    const order = await createOrder({name:`WEB-${Date.now()}`,partnerId:parseInt(partnerId)});
    if(order?.id){
      for(const item of cart){
        await createOrderLine({orderId:order.id,productId:item.productId,name:item.name,productUomQty:item.qty,priceUnit:item.price});
      }
      setOrderPlaced(order.id);
      setCart([]);
    }
  };

  if(orderPlaced) return <div className="text-center py-20"><div className="text-6xl mb-4">✅</div><h1 className="text-2xl font-bold mb-2">Order Placed!</h1><p className="text-gray-500 mb-6">Order #{orderPlaced} confirmed.</p><button onClick={()=>{setOrderPlaced(null);navigate("/orders")}} className="px-6 py-2 bg-blue-600 text-white rounded-lg">View Orders</button></div>;

  return <div>
    <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white rounded-2xl p-8 mb-6">
      <h1 className="text-3xl font-bold">Store</h1>
      <p className="mt-2 text-blue-100">{allProducts?.length??0} products available</p>
      <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="🔍 Search products..." className="mt-4 w-full max-w-md px-4 py-2.5 rounded-lg text-gray-900 outline-none focus:ring-2 focus:ring-blue-300" />
    </div>

    <div className="flex gap-6">
      <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((p:any) => <div key={p.id} className="bg-white rounded-xl shadow-sm border p-4 hover:shadow-md transition-shadow">
          <div className="bg-gray-50 rounded-lg h-32 mb-3 flex items-center justify-center text-4xl">📦</div>
          <h3 className="font-semibold">{p.name}</h3>
          <p className="text-xs text-gray-400 mt-1 capitalize">{p.type}</p>
          <div className="flex items-center justify-between mt-3">
            <span className="text-xl font-bold text-blue-600">${(p.list_price??0).toFixed(2)}</span>
            <button onClick={()=>addToCart(p)} className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">+ Add</button>
          </div>
        </div>)}
        {filtered.length===0 && <p className="text-gray-400 col-span-3 text-center py-12">{search?"No products match your search":"Loading products..."}</p>}
      </div>

      <div className="w-72 shrink-0">
        <div className="bg-white rounded-xl shadow-sm border p-4 sticky top-6">
          <h3 className="font-bold mb-3">Cart ({cartCount})</h3>
          <div className="space-y-2 max-h-64 overflow-auto mb-3">
            {cart.map((item,idx) => <div key={idx} className="flex items-center justify-between text-sm">
              <div><p className="truncate max-w-32">{item.name}</p><span className="text-gray-400 text-xs">x{item.qty}</span></div>
              <span className="font-medium">${(item.price*item.qty).toFixed(2)}</span>
              <button onClick={()=>setCart(cart.filter((_,i)=>i!==idx))} className="text-red-400 text-xs hover:text-red-600">✕</button>
            </div>)}
            {cart.length===0 && <p className="text-gray-400 text-xs text-center py-4">Your cart is empty</p>}
          </div>
          {cart.length>0 && <>
            <select value={partnerId} onChange={e=>setPartnerId(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm mb-3 outline-none">
              <option value="">Select customer...</option>
              {allPartners?.map((p:any) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <div className="flex justify-between font-bold text-lg border-t pt-2 mb-3"><span>Total</span><span>${total.toFixed(2)}</span></div>
            <button onClick={checkout} disabled={!partnerId} className="w-full py-2.5 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50">Place Order</button>
          </>}
        </div>
      </div>
    </div>
  </div>;
}
